import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Link,
  GlobalStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { IoArrowBack, IoChevronDown } from "react-icons/io5";
import { School as SchoolIcon, Shield as ShieldIcon } from "@mui/icons-material";
import { adminColors } from "../../theme/adminTheme";
import { FONT_STACK, BORDER_COLOR } from "../../theme/uiConstants";

export { FONT_STACK, BORDER_COLOR };

// Focus ring used on interactive text (ToC links, cross-links) sitting on
// white/light surfaces — keeps focus-visible legible against adminColors.accent
// text without relying on the browser default outline color.
const focusRingSx = {
  "&:focus-visible": {
    outline: `2px solid ${adminColors.accent}`,
    outlineOffset: 3,
    borderRadius: "4px",
  },
};

/**
 * Numbered section wrapper. `id` must match the corresponding entry in the
 * `sections` array passed to LegalPageLayout so the table of contents can
 * link to it. tabIndex={-1} + outline: none is deliberate here: this is a
 * scroll-anchor target (receives programmatic focus when a ToC link is
 * activated, so assistive tech announces the right heading), not a
 * keyboard-tabbable control — showing a focus ring on it would look like a
 * stray tab stop to sighted mouse/touch users.
 */
export function Section({ id, number, title, children }) {
  return (
    <Box
      component="section"
      id={id}
      tabIndex={-1}
      sx={{ scrollMarginTop: "96px", outline: "none", mb: 5, "&:last-of-type": { mb: 0 } }}
    >
      <Typography
        component="h2"
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.15rem", sm: "1.3rem" },
          color: adminColors.ink,
          letterSpacing: "-0.01em",
          mb: 1.75,
          display: "flex",
          gap: 1,
        }}
      >
        <Box component="span" sx={{ color: adminColors.accent }}>
          {number}.
        </Box>
        {title}
      </Typography>
      <Box
        sx={{
          color: adminColors.inkSoft,
          fontSize: "0.98rem",
          lineHeight: 1.8,
          "& p": { m: 0, mb: 1.75 },
          "& p:last-child": { mb: 0 },
          "& ul": { m: 0, mb: 1.75, pl: 3 },
          "& li": { mb: 0.75 },
          "& strong": { color: adminColors.ink, fontWeight: 600 },
          "& a": {
            color: adminColors.accentDark,
            fontWeight: 600,
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            cursor: "pointer",
          },
          "& a:hover": { color: adminColors.accent },
          "& a:focus-visible": {
            outline: `2px solid ${adminColors.accent}`,
            outlineOffset: 2,
            borderRadius: "3px",
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** Single highlighted callout for the one fact users most need to notice
 * before reading the rest of the document — used sparingly, not as a
 * general-purpose box, so it keeps its weight as a signal. */
export function Callout({ children }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        bgcolor: adminColors.accentLight,
        border: `1px solid ${adminColors.accentBorder}`,
        borderRadius: "10px",
        px: { xs: 2, sm: 2.5 },
        py: 2,
        mb: { xs: 5, sm: 6 },
      }}
    >
      <ShieldIcon sx={{ color: adminColors.accentDark, fontSize: 20, mt: "2px", flexShrink: 0 }} />
      <Typography sx={{ color: adminColors.ink, fontSize: "0.92rem", lineHeight: 1.7 }}>
        {children}
      </Typography>
    </Box>
  );
}

/** Short, casual restatement of a legally-precise paragraph above it — for
 * the 2-3 sections students are actually likely to read (what deletion
 * does, who can see chats). Deliberately lighter-weight than Callout so the
 * two don't compete: Callout marks "the one fact you must not miss" at the
 * top of a page, Plain marks "here's that same paragraph in plain English"
 * inline within a section. */
export function Plain({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "0.9rem",
        color: adminColors.inkSoft,
        borderLeft: `2px solid ${adminColors.accentBorder}`,
        pl: 1.5,
        mb: 1.75,
        lineHeight: 1.6,
      }}
    >
      <Box component="span" sx={{ fontWeight: 700, color: adminColors.ink }}>
        In short:
      </Box>{" "}
      {children}
    </Typography>
  );
}

/**
 * In-body link between /privacy and /terms (optionally to a specific
 * section, e.g. `/privacy#chat-retention`). A plain <a href> here would do a
 * full page reload — fine for the standalone legal pages themselves, but
 * this component also gets reused anywhere a full reload would cost more
 * (discarding scroll/accordion state). Handles the hash-scroll itself since
 * client-side navigation doesn't get the browser's native "jump to
 * fragment" behavior.
 */
export function CrossDocLink({ to, children }) {
  const navigate = useNavigate();
  return (
    <a
      href={to}
      onClick={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

// Exported (and named generically, not "Legal...") so other minimal,
// brand-shell pages outside the legal docs (e.g. the 404 page) can reuse
// the same header instead of a second, slightly-different copy of the logo
// mark + wordmark.
export function BrandHeader() {
  const navigate = useNavigate();
  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        px: 3,
        py: 2,
        backgroundColor: "white",
        borderBottom: `1px solid ${BORDER_COLOR}`,
      }}
    >
      <Link
        component="button"
        type="button"
        onClick={() => navigate("/")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          textDecoration: "none",
          cursor: "pointer",
          ...focusRingSx,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            backgroundColor: adminColors.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SchoolIcon sx={{ color: "white", fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 17, color: adminColors.ink, letterSpacing: "-0.01em" }}>
          LearnAndMasterAI
        </Typography>
      </Link>
    </Box>
  );
}

function LegalFooter({ crossLinkTo, crossLinkLabel }) {
  const navigate = useNavigate();
  return (
    <Box component="footer" sx={{ borderTop: `1px solid ${BORDER_COLOR}`, py: 4, mt: { xs: 6, sm: 8 } }}>
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            &copy; 2026 LearnAndMasterAI. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate(crossLinkTo)}
              sx={{ fontSize: "0.875rem", fontWeight: 600, color: adminColors.accent, cursor: "pointer", ...focusRingSx }}
            >
              {crossLinkLabel}
            </Link>
            <Link
              href="mailto:info@learnandmasterai.com"
              sx={{ fontSize: "0.875rem", fontWeight: 600, color: adminColors.accent, ...focusRingSx }}
            >
              Contact us
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// Row styling shared by both ToC renderings (mobile accordion + desktop
// sticky rail): full-width, 44px-minimum tappable row rather than a bare
// line of text with a few px of row gap — matches the ChatSidebarItem
// convention for list-row tap targets.
function TocList({ sections }) {
  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {sections.map((s) => (
        <Box component="li" key={s.id}>
          <Link
            href={`#${s.id}`}
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: 44,
              px: 1,
              mx: -1,
              borderRadius: "8px",
              fontSize: "0.85rem",
              color: adminColors.inkSoft,
              textDecoration: "none",
              transition: "background-color 0.12s ease, color 0.12s ease",
              "&:hover": { color: adminColors.accent, backgroundColor: adminColors.accentLight },
              ...focusRingSx,
            }}
          >
            {s.number}. {s.title}
          </Link>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Shared shell for /privacy and /terms. Renders the standard header,
 * title block, an in-page table of contents built from `sections`
 * (must line up with the `id`s used on each <Section>) — collapsed by
 * default on mobile so the page content starts right after the intro
 * instead of after an 11-item list, and a sticky rail on desktop — the
 * page content, and a footer with a cross-link to the sibling legal page.
 */
export default function LegalPageLayout({
  title,
  lastUpdated,
  intro,
  sections = [],
  crossLinkTo,
  crossLinkLabel,
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Client-side nav (CrossDocLink, or a bookmarked /privacy#section URL)
  // doesn't get the browser's native "jump to fragment" behavior, since
  // there's no real page load / hashchange event to trigger it. Do it
  // ourselves once the target section has mounted.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      el.focus({ preventScroll: true });
    }
  }, [location.hash, location.pathname]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: adminColors.surface,
        fontFamily: FONT_STACK,
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      <GlobalStyles
        styles={{
          html: { scrollBehavior: "smooth" },
          "@media (prefers-reduced-motion: reduce)": {
            html: { scrollBehavior: "auto" },
          },
        }}
      />

      <BrandHeader />

      <Container maxWidth="md" sx={{ pt: { xs: 4, sm: 6 }, pb: { xs: 2, sm: 3 } }}>
        <Link
          component="button"
          type="button"
          onClick={() => navigate("/")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: "0.875rem",
            fontWeight: 600,
            color: adminColors.inkSoft,
            cursor: "pointer",
            mb: 3,
            "&:hover": { color: adminColors.accent },
            ...focusRingSx,
          }}
        >
          <IoArrowBack size={15} />
          Back to home
        </Link>

        <Typography
          component="h1"
          sx={{
            fontWeight: 700,
            color: adminColors.ink,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            fontSize: { xs: "2rem", sm: "2.75rem" },
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: adminColors.inkSoft, fontWeight: 600, mb: 3 }}>
          Last updated: {lastUpdated}
        </Typography>

        {intro && (
          <Typography sx={{ color: adminColors.inkSoft, fontSize: "1.02rem", lineHeight: 1.75, mb: { xs: 4, sm: 5 } }}>
            {intro}
          </Typography>
        )}
      </Container>

      <Container maxWidth="md" sx={{ pb: { xs: 6, sm: 8 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "200px 1fr" },
            gap: { xs: 0, md: 6 },
            bgcolor: "white",
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "14px",
            p: { xs: 3, sm: 5 },
          }}
        >
          <Box
            component="nav"
            aria-label="Sections"
            sx={{
              position: { md: "sticky" },
              top: { md: "24px" },
              alignSelf: "start",
              order: { xs: -1, md: 0 },
            }}
          >
            {/* Mobile: closed-by-default disclosure. Content starts right
                after the intro; the ToC is one tap away, not a scroll
                obstacle. */}
            <Accordion
              disableGutters
              elevation={0}
              square={false}
              sx={{
                display: { xs: "block", md: "none" },
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: "10px !important",
                mb: 4,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<IoChevronDown size={15} color={adminColors.inkSoft} />}
                sx={{
                  minHeight: 48,
                  "& .MuiAccordionSummary-content": { my: 1 },
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: adminColors.ink }}>
                  Jump to section
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TocList sections={sections} />
              </AccordionDetails>
            </Accordion>

            {/* Desktop: always-visible sticky rail */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: adminColors.inkSoft,
                  mb: 1,
                  px: 1,
                }}
              >
                On this page
              </Typography>
              <TocList sections={sections} />
            </Box>
          </Box>

          <Box>{children}</Box>
        </Box>
      </Container>

      <LegalFooter crossLinkTo={crossLinkTo} crossLinkLabel={crossLinkLabel} />
    </Box>
  );
}
